import React, { useState, useRef, useEffect } from 'react'
import { Nav } from 'react-bootstrap'
import PropTypes from 'prop-types'
import { useLazyQuery } from '@apollo/client'
import dynamic from 'next/dynamic'
import Trans from 'next-translate/Trans'

import navStyles from '@shared-components/commonNav/style.module.scss'
import styles from '@shared-components/fixtureContent/style.module.scss'

import { CURRENT_MATCHES_V2 } from '@graphql/fixtures/fixtures.query'
import { getTimeZone, formatDate, getDateRange } from '@utils'
import { fixtureLoader } from '@shared/libs/allLoader'
import { useRouter } from 'next/router'
import CustomLink from '../customLink'
import { allRoutes } from '@shared/constants/allRoutes'

const DateSlider = dynamic(() => import('@shared-components/dateSlider'))
const FixturesItems = dynamic(() => import('@shared-components/series/fixturesItems'), { loading: () => fixtureLoader() })
// const FixtureData = dynamic(() => import('@shared/components/fixtureContent/fixtureData'))
const NoData = dynamic(() => import('@noData'))
const Ads = dynamic(() => import('@shared/components/ads'))

export default function ScheduleContent({ data, currentDate }) {
  const router = useRouter()
  const { date } = router.query
  const { dStartDate, dEndDate, dates } = getDateRange(0, 16, date || currentDate)
  const nextDate = new Date(dEndDate)
  nextDate.setDate(nextDate.getDate() + 1)
  const [fixtureData, setFixtureData] = useState(getDateWiseMatches(dates, data?.listAllFixturesV2 || []))
  const [currentDay, setCurrentDay] = useState(formatDate(dStartDate, 'YYYY-MM-DD'))
  const payloads = useRef({ eCategory: 'i', dStartDate, dEndDate, sTimezone: getTimeZone(), nSkip: 1 })
  const totalMatchesRendered = useRef(0)

  const [updateScheduleMatch, { loading, data: rawData }] = useLazyQuery(CURRENT_MATCHES_V2, {
    onCompleted: (d) => {
      totalMatchesRendered.current = 0
      setFixtureData(getDateWiseMatches(dates, d?.listAllFixturesV2 || []))
    }
  })

  const handleCategoryFilter = (value) => {
    payloads.current = { ...payloads.current, eCategory: value, nSkip: 1 }
    update()
  }

  function update() {
    updateScheduleMatch({ variables: { input: payloads.current } })
  }

  function handleNext(next) {
    const { dStartDate, dEndDate } = getDateRange(0, 15, next)
    payloads.current = { ...payloads.current, dStartDate, dEndDate }
    update()
    setCurrentDay(formatDate(next, 'YYYY-MM-DD'))
  }

  function getDateWiseMatches(days, matches) {
    // Initialize result object
    const matchByDay = {}

    // First pass: initialize all days with empty arrays
    days.forEach((day) => {
      const formattedDay = formatDate(day, 'YYYY-MM-DD')
      matchByDay[formattedDay] = []
    })

    // Second pass: iterate through matches only once and assign to appropriate day
    matches.forEach((match) => {
      const matchDate = formatDate(match.dStartDate, 'YYYY-MM-DD')

      // Only add the match if we have this date in our days array
      if (matchDate in matchByDay) {
        matchByDay[matchDate].push(match)
      }
    })

    return { ...matchByDay }
  }

  useEffect(() => {
    if (!router.query?.date || router.query?.date === formatDate(currentDate, 'YYYY-MM-DD')) {
      totalMatchesRendered.current = 0
      payloads.current = { ...payloads.current, dStartDate, dEndDate, eCategory: 'i' }
      setCurrentDay(formatDate(dStartDate, 'YYYY-MM-DD'))
      setFixtureData(getDateWiseMatches(dates, data?.listAllFixturesV2 || []))
    } else if (router.query?.date && router.query?.date !== formatDate(currentDate, 'YYYY-MM-DD')) {
      handleNext(router.query.date)
    }
  }, [router.query])

  const isData = router.query?.date && router.query?.date !== formatDate(currentDate, 'YYYY-MM-DD') ? rawData : data
  return (
    <div className="position-relative">
      <Nav
        className={`${navStyles.commonNav} ${navStyles.themeLightNav} ${navStyles.stickyNav} ${styles.catNav} text-uppercase equal-width-nav scroll-list flex-nowrap text-nowrap`}
        variant="pills"
      >
        <Nav.Item className={`${navStyles.item}`}>
          <a className={`${payloads.current.eCategory === 'i' ? navStyles.active : ''} nav-link`} onClick={() => handleCategoryFilter('i')}>
            <Trans i18nKey="common:International" />
          </a>
        </Nav.Item>
        <Nav.Item className={`${navStyles.item}`}>
          <a className={`${payloads.current.eCategory === 'l' ? navStyles.active : ''} nav-link`} onClick={() => handleCategoryFilter('l')}>
            <Trans i18nKey="common:League" />
          </a>
        </Nav.Item>
        <Nav.Item className={`${navStyles.item}`}>
          <a className={`${payloads.current.eCategory === 'd' ? navStyles.active : ''} nav-link`} onClick={() => handleCategoryFilter('d')}>
            <Trans i18nKey="common:Domestic" />
          </a>
        </Nav.Item>
        <Nav.Item className={`${navStyles.item}`}>
          <a className={`${payloads.current.eCategory === 'w' ? navStyles.active : ''} nav-link`} onClick={() => handleCategoryFilter('w')}>
            <Trans i18nKey="common:Women" />
          </a>
        </Nav.Item>
      </Nav>
      <DateSlider
        className={styles.fixtureDateSlider}
        dayArray={dates}
        selectedDay={currentDay}
        disable={Object.entries(fixtureData)
          .filter(([_, matches]) => matches.length === 0)
          .map(([date]) => date)}
        handleMonth={(e) => {
          const d = formatDate(e, 'YYYY-MM-DD')
          const ele = document.getElementById(d)
          if (ele) {
            window.scrollTo({
              top: window.innerWidth > 576 ? ele.offsetTop + 200 : ele.offsetTop + 70,
              behavior: 'smooth'
            })
          }
          setCurrentDay(d)
        }}
        isSticky
      />
      {isData?.listAllFixturesV2?.length > 0 &&
        Object.keys(fixtureData).map((day, i) => {
          if (fixtureData?.[day]?.length === 0) return null
          return (
            <div key={i} id={day}>
              <div className="bg-secondary text-light p-2 rounded mb-1">{formatDate(day, 'EEE, MMMM DD, YYYY')}</div>
              {fixtureData?.[day].length === 0 && <p>No matches scheduled</p>}
              {fixtureData?.[day].map((fixture, j) => {
                totalMatchesRendered.current += 1
                return (
                  <React.Fragment key={fixture?._id}>
                    <FixturesItems fixture={fixture} id={fixture?._id} isSeriesShow />
                    {totalMatchesRendered.current === 1 && (
                      <Ads
                        id="div-ad-gpt-138639789-Desktop_Fix_ATF"
                        adIdDesktop="Crictracker_New_Desktop_Fix_MID_728x90"
                        adIdMobile="Crictracker_New_Mobile_FIX_MID_300x250"
                        dimensionDesktop={[728, 90]}
                        dimensionMobile={[300, 250]}
                        slotId="23148549803"
                        mobile
                        className="mb-3 text-center"
                      />
                    )}
                    {totalMatchesRendered.current === 5 && (
                      <Ads
                        id="div-ad-gpt-138639789-1646637094-0"
                        adIdDesktop="Crictracker_New_Desktop_Fix_MID_728x90"
                        adIdMobile="Crictracker_New_Mobile_FIX_BTF_300x250"
                        slotId="23148549803"
                        dimensionDesktop={[728, 90]}
                        dimensionMobile={[300, 250]}
                        mobile
                        className="mb-3 text-center"
                      />
                    )}
                  </React.Fragment>
                )
              })}
            </div>
          )
        })}
      {/* <FixtureData data={fixtureData} id={fixtureData?._id} /> */}
      {!loading && isData?.listAllFixturesV2?.length === 0 && <NoData title="No matches scheduled" />}
      {loading && fixtureLoader()}
      <div className="text-center">
        <CustomLink
          className="theme-btn small-btn"
          shallow={true}
          href={{
            pathname: allRoutes.cricketSchedule,
            query: { date: formatDate(nextDate, 'YYYY-MM-DD') }
          }}
          // onClick={() => handleNext(nextDate)}
        >
          View next 15 days
        </CustomLink>
      </div>
    </div>
  )
}

ScheduleContent.propTypes = {
  data: PropTypes.object,
  currentDate: PropTypes.string,
  defaultQuery: PropTypes.object
}
